<template>
  <div class="dealer-detail-page">
    <!-- Barra superior con título y acciones -->
    <v-card class="mb-4 elevation-2">
      <v-app-bar flat dense color="primary" dark>
        <v-btn icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>Detalles del Concesionario</v-toolbar-title>
        <v-spacer></v-spacer>

        <v-btn icon @click="showDealerForm(dealer)" v-if="!loading">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-app-bar>
    </v-card>

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="50" width="5"></v-progress-circular>
      <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-1">
        Cargando detalles del concesionario...
      </div>
    </div>

    <template v-else>
      <v-container>
        <v-row>
          <!-- Panel principal de información del concesionario -->
          <v-col cols="12" md="8" lg="7">
            <v-card class="elevation-1">
              <!-- Cabecera con datos principales -->
              <div class="dealer-header pa-4">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="64" class="mr-4">
                    <v-icon size="36" dark>mdi-store</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h5 font-weight-bold">{{ dealer.name }}</div>

                  </div>
                </div>
              </div>

              <v-divider></v-divider>

              <!-- Información básica y de contacto -->
              <v-card-text class="py-4">
                <v-row>
                  <!-- Columna Información básica -->
                  <v-col cols="12" md="6">
                    <h3 class="subtitle-1 font-weight-bold mb-3">
                      <v-icon color="primary" small class="mr-2">mdi-information</v-icon>
                      Información básica
                    </h3>

                    <v-list dense class="transparent pa-0">
                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-store</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Nombre</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ dealer.name }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0" v-if="dealer.website">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-web</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Sitio web</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a :href="prependHttpIfNeeded(dealer.website)" target="_blank" class="text-decoration-none">
                              {{ dealer.website }}
                            </a>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-account-tie</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Manager</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ adminName || 'No asignado' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0" v-if="dealer.theme">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-palette</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Tema</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ getThemeLabel(dealer.theme) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>

                  <!-- Columna Información de contacto -->
                  <v-col cols="12" md="6">
                    <h3 class="subtitle-1 font-weight-bold mb-3">
                      <v-icon color="primary" small class="mr-2">mdi-card-account-phone</v-icon>
                      Información de contacto
                    </h3>

                    <v-list dense class="transparent pa-0">
                      <v-list-item class="px-0" v-if="dealer.address">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-map-marker</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dirección</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ dealer.address }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-phone</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Teléfono</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="dealer.phone" :href="`tel:${dealer.phone}`" class="text-decoration-none">
                              {{ dealer.phone }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0" v-if="dealer.mobile_phone">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-cellphone</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Teléfono móvil</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a :href="`tel:${dealer.mobile_phone}`" class="text-decoration-none">
                              {{ dealer.mobile_phone }}
                            </a>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-email</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Correo electrónico</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="dealer.email" :href="`mailto:${dealer.email}`" class="text-decoration-none">
                              {{ dealer.email }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Panel lateral con logo y usuarios asociados -->
          <v-col cols="12" md="4" lg="5">
            <!-- Tarjeta de estadísticas -->
            <v-card class="elevation-1">
              <v-card-title class="pb-1">
                <v-icon left color="primary">mdi-account-group</v-icon>
                Usuarios asociados
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="p-0">
                <dealer-users :dealer-id="dealer.id" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- Formulario de edición -->
    <dealer-form v-if="visibleDealerForm" v-model="editItem" @save="updateDataTable"
      @cancel="visibleDealerForm = false"></dealer-form>
  </div>
</template>

<script>
import DealerForm from './DealerForm.vue'
import DealerUsers from './DealerUsers.vue'
import DealerService from '@/services/DealerService'
import UserService from '@/services/UserService'

export default {
  components: {
    DealerForm,
    DealerUsers
  },
  data () {
    return {
      loading: true,
      dealer: null,
      visibleDealerForm: false,
      editItem: null,
      adminName: '',
      themes: [
        { value: 'light', text: 'Claro' },
        { value: 'dark', text: 'Oscuro' },
        { value: 'blue', text: 'Azul' },
        { value: 'green', text: 'Verde' },
        { value: 'red', text: 'Rojo' }
      ]
    }
  },
  async created () {
    await this.loadDealerDetails()
  },
  methods: {
    async loadDealerDetails () {
      try {
        this.loading = true
        const dealerId = this.$route.params.dealer_id
        const { data } = await DealerService.view({ id: dealerId })
        this.dealer = data.data

        // Obtener el nombre del administrador
        if (this.dealer.admin_id) {
          const adminData = await UserService.viewUserDealer(this.dealer.admin_id)
          this.adminName = `${adminData.data.data.name} ${adminData.data.data.lastname}`
        }
      } catch (error) {
        console.error('Error al cargar los detalles del concesionario:', error)
        this.$toast.error('Error al cargar los detalles del concesionario')
      } finally {
        this.loading = false
      }
    },

    showDealerForm (item) {
      this.editItem = item
      this.visibleDealerForm = true
    },

    updateDataTable () {
      this.visibleDealerForm = false
      this.loadDealerDetails()
      this.$toast.success('Concesionario actualizado con éxito')
    },

    getThemeLabel (themeValue) {
      const theme = this.themes.find(t => t.value === themeValue)
      return theme ? theme.text : themeValue
    },

    prependHttpIfNeeded (url) {
      if (!url) return '#'
      return url.match(/^https?:\/\//) ? url : `http://${url}`
    }
  }
}
</script>

<style scoped>
.dealer-header {
  background-color: rgba(0, 0, 0, 0.01);
}

.v-list-item {
  min-height: 0;
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Transiciones y efectos */
.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}
</style>
